import { useState, useEffect } from "react";
import { GridLayout } from "../components/layout";
import { StandardComp } from "../atoms/standardComp";

export const MatchChoice = ({ data, answerContent }) => {
  const [answers, setAnswers] = useState([]);
  const [selectedLeft, setSelectedLeft] = useState(null);
  const [matchedPairs, setMatchedPairs] = useState({});
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth <= 768 : false
  );

  const defaultColors = ["var(--error-50)", "var(--primary-50)", "var(--warning-50)"];
  const darkerColors = ["var(--error-500)", "var(--primary-500)", "var(--warning-500)"];

  useEffect(() => {
    if (data?.length > 0) {
      setAnswers(data.map((answer, index) => ({ ...answer, id: index })));
    }
  }, [data]);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const isSelectedOrMatched = (index) => selectedLeft === index || matchedPairs[index] !== undefined;
  const getCheckCircleStyles = (index) => {
    const color = isSelectedOrMatched(index) ? darkerColors[index % darkerColors.length] : "transparent";
    return { borderColor: color, color };
  };

  const getItemStyle = (isSelected, index) => ({
    border: `1px solid ${isSelected ? darkerColors[index % darkerColors.length] : "var(--divider-border-color)"}`,
    backgroundColor: isSelected ? defaultColors[index % defaultColors.length] : "transparent",
    transform: isSelected ? "scale(1.03)" : "scale(1)",
    boxShadow: isSelected ? "0px 4px 8px rgba(0, 0, 0, 0.1)" : "none",
    opacity: isSelected ? 1 : 0.85,
    color: isSelected ? "var(--standard-text-color)" : "var(--standard-text-color-light)",
    width: "100%",  
    boxSizing: "border-box",
    alignContent:"center"
  });

  const handleLeftClick = (leftIndex) => {
    setMatchedPairs((prevPairs) => {
      let updatedPairs = { ...prevPairs };
      if (updatedPairs[leftIndex]) {
        const matchedRightOption = updatedPairs[leftIndex]?.option;
        if (matchedRightOption) {
          updatedPairs = Object.fromEntries(
            Object.entries(updatedPairs).filter(
              ([key, value]) => key !== leftIndex.toString() && value.option !== matchedRightOption
            )
          );
        }
        setSelectedLeft(null);
      } else {
        setSelectedLeft(leftIndex);
      }
      return updatedPairs;
    });
  };

  const handleRightClick = (rightIndex) => {
    if (selectedLeft === null) return;
    setMatchedPairs((prev) => {
      const updatedPairs = { ...prev };
      const existingLeftIndex = Object.keys(updatedPairs).find(
        (key) => updatedPairs[key]?.option === answers[rightIndex].option
      );

      if (existingLeftIndex) {
        delete updatedPairs[existingLeftIndex];
      }

      updatedPairs[selectedLeft] = {
        answer: answers[selectedLeft].answer,
        option: answers[rightIndex].option,
      };

      const pairObj = Object.keys(updatedPairs).map((key) => updatedPairs[key]);
      answerContent(pairObj);

      setSelectedLeft(null);

      return updatedPairs;
    });
  };

  const outerColumns = isMobile ? "1fr 1fr" : "repeat(auto-fit, minmax(100px, 1fr))";
  const outerGap = isMobile ? "16px" : "35px";
  const innerRowTemplate = isMobile ? `repeat(${answers.length}, minmax(56px, auto))` : "auto";

  return (
    <div className="match standard-margin">
      <GridLayout
        columnsTemplate={outerColumns}
        gap={outerGap}
        style={{ width: "100%", justifyContent: "center" }}
      >
        <GridLayout className="match-left" rowTemplate={innerRowTemplate} gap="16px">
          {answers.map((answer, index) => (
            <StandardComp
              key={`left-${answer.id}`}
              content={
                <div className="horizontal">
                  <div className="check-circle" style={getCheckCircleStyles(index)}>
                    {isSelectedOrMatched(index) && <span style={getCheckCircleStyles(index)}>{index + 1}</span>}
                  </div>
                  <p className="match-text">{answer.answer}</p>
                </div>
              }
              onClick={() => handleLeftClick(index)}
              style={getItemStyle(isSelectedOrMatched(index), index)}
            />
          ))}
        </GridLayout>

        <GridLayout className="match-right" rowTemplate={innerRowTemplate} gap="16px">
          {answers.map((answer, rightIndex) => {
            const leftIndex = Object.keys(matchedPairs).find(
              (key) => matchedPairs[key]?.option === answer.option
            );

            return (
              <StandardComp
                key={`right-${answer.id}`}
                content={
                  <div className="horizontal">
                    <div className="check-circle" style={leftIndex ? getCheckCircleStyles(leftIndex) : { borderColor: "transparent" }}>
                      {leftIndex && <span style={getCheckCircleStyles(leftIndex)}>{parseInt(leftIndex) + 1}</span>}
                    </div>
                    <p className="match-text">{answer.option}</p>
                  </div>
                }
                onClick={() => handleRightClick(rightIndex)}
                style={getItemStyle(leftIndex !== undefined, leftIndex)}
              />
            );
          })}
        </GridLayout>
      </GridLayout>
    </div>
  );
};

export default MatchChoice;
